/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */
import React, { FC, useEffect, useState } from 'react'
import { Box } from '@moonpig/launchpad-components'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { ModuleContext, ModuleImages } from '../types'
import { useTracking } from '../../contexts/tracking'
import { useProductStore } from '../../contexts/product'
import { useFavouritesState, checkFavourited } from '../../contexts/favourites'
import { isCardProduct } from '../../utils/isCardProduct'
import { GiftImageCarousel } from '../../components/GiftImageCarousel'
import { ImageCarouselVariant } from '../../components/ImageCarouselVariant'
import { useHandleFavourite } from '../../utils/favourites'
import { getCardFormat } from './card-format'
import {
  CARD_INSIDE_LEFT_INDEX,
  CARD_INSIDE_RIGHT_INDEX,
  FORMAT_LANDSCAPE_CARD,
  FORMAT_POST_CARD,
  FORMAT_SQUARE_CARD,
  GROUP_CARD_IMAGES,
} from './constants'
import { ImageCarouselVariantFrictionlessFlow } from '../../components/ImageCarouselVariant/ImageCarouselVariantFrictionlessFlow'
import thumbnailSquare from './assets/thumbnail-square.png'
import thumbnailLandscape from './assets/thumbnail-landscape.png'
import thumbnailPortrait from './assets/thumbnail-portrait.png'
import { useDispatchCutOffQuery } from '../../queries/DispatchCutOff'

type Props = {
  module: ModuleImages
  context: ModuleContext
}

export const ImagesModule: FC<Props> = () => {
  const tracking = useTracking()
  const { favourites } = useFavouritesState()
  const handleFavourites = useHandleFavourite({
    removeWithConfirmation: false,
    tracking: { pageLocation: '', totalCount: 1 },
  })
  const [{ product, isGroupCardSelected }] = useProductStore()

  const dispatchCutOff = useDispatchCutOffQuery(product.category.department)

  const isFrictionlessFlowEnabled = useExperiment('frictionless-flow-v1', {
    fallback: false,
  })

  const ImageCarouselVariantComponent = isFrictionlessFlowEnabled
    ? ImageCarouselVariantFrictionlessFlow
    : ImageCarouselVariant

  const cardFormat = getCardFormat(product)

  const [initialIndex, setInitialIndex] = useState(0)

  const isDynamicAgeEnabled = useExperiment('search-use-dynamic-images', {
    fallback: false,
  })

  const getCardImageUrl = (
    image: { url: string },
    imageIndex: number,
  ): string => {
    if (
      cardFormat !== FORMAT_POST_CARD &&
      isGroupCardSelected &&
      [CARD_INSIDE_LEFT_INDEX, CARD_INSIDE_RIGHT_INDEX].includes(imageIndex)
    ) {
      return GROUP_CARD_IMAGES[cardFormat][imageIndex - 1]
    }

    if (
      isDynamicAgeEnabled &&
      imageIndex === 0 &&
      product.dynamicImages?.large?.webp?.[0]?.url
    ) {
      return product.dynamicImages?.large?.webp?.[0]?.url
    }

    return image.url
  }

  const getImages = () => {
    const images = product.masterVariant.images.map((image, imageIndex) => ({
      url: getCardImageUrl(image, imageIndex),
    }))

    if (!isFrictionlessFlowEnabled) return images

    switch (cardFormat) {
      case FORMAT_SQUARE_CARD:
        return [{ url: thumbnailSquare }, ...images]
      case FORMAT_LANDSCAPE_CARD:
        return [{ url: thumbnailLandscape }, ...images]
      default:
        return [{ url: thumbnailPortrait }, ...images]
    }
  }

  useEffect(() => {
    setInitialIndex(0)
  }, [product])

  useEffect(() => {
    setInitialIndex(isGroupCardSelected ? 1 : 0)
  }, [isGroupCardSelected])

  const isCard = isCardProduct(product.category.department)

  const imageCarousel = () => {
    if (isCard) {
      const images = getImages()

      return (
        <>
          <ImageCarouselVariantComponent
            productId={product.id}
            title={product.title}
            images={images}
            initialSelectedIndex={initialIndex}
            isFavourited={checkFavourited(favourites, product)}
            onFavouriteToggle={() =>
              handleFavourites(product, checkFavourited(favourites, product), 0)
            }
            onImageSelected={({ index }) => {
              tracking.selectContent({
                component: 'view product image',
                label: product.id,
                action: `${index + 1}/${images.length}`,
              })
            }}
            dispatchCutOff={dispatchCutOff}
          />
        </>
      )
    }

    return (
      <GiftImageCarousel
        images={product.masterVariant.images.map(({ url }) => url)}
        title={product.title}
        isFavourited={checkFavourited(favourites, product)}
        onFavouriteToggle={() =>
          handleFavourites(product, checkFavourited(favourites, product), 0)
        }
      />
    )
  }

  return (
    <Box mb={6} data-testid="module-images" position="relative">
      {imageCarousel()}
    </Box>
  )
}
